/**
 * Species table (doc 06), resolved from content. Regular fauna and bosses use
 * one shape; boss gimmicks are data the sim's clockwork reads.
 *
 * Every optional field defaults to "this species does not do that", so a
 * behaviour is opt-in per row and the sim can ask `sp.dive !== null` instead of
 * knowing which fish is which.
 */

import enemiesJson from '../content/enemies.json' with { type: 'json' };

/** Doc 06: on death, a piñata releases its contents. */
export interface DeathSpawn {
  species: string;
  count: number;
}

/** The Pale Lure's cloak metronome (doc 06): n seconds hidden, m seconds not. */
export interface CloakCycle {
  cloakedSeconds: number;
  visibleSeconds: number;
}

/** Damage reduction while enough of its own escort is still alive (doc 06). */
export interface GuardRule {
  species: string;
  minAlive: number;
  damageMult: number;
  radius: number;
}

/** The Leviathan Calf's breach dive: gone for `seconds`, back `tiles` ahead. */
export interface DiveRule {
  everySeconds: number;
  seconds: number;
  tiles: number;
}

/** A jaw that treats a wall as a door: burst damage on a fixed clock (doc 06). */
export interface BiteRule {
  damage: number;
  everySeconds: number;
}

/** Doc 06: a singer makes everything around it worse, and hangs back to do it. */
export interface AuraRule {
  radius: number;
  speedBonus: number;
  healPerSecond: number;
  /** Tiles it keeps behind the front line — a support unit never leads. */
  standoff: number;
}

/** The Mother of Threads: she leaves her regeneration lying around (doc 06). */
export interface ThreadRule {
  species: string;
  count: number;
  everySeconds: number;
  radius: number;
  /** HP/s she gains for each live node standing inside `radius`. */
  regenPer: number;
}

/** The Hadal Sovereign's Pressure Pulse: the lights go out for everyone (doc 06). */
export interface PulseRule {
  everySeconds: number;
  disableSeconds: number;
  /** Below this HP fraction the clock shortens to `hastenedSeconds`. */
  hastenBelow: number;
  hastenedSeconds: number;
}

export interface SpeciesDef {
  name: string;
  hp: number;
  speed: number;
  dps: number;
  armor: number;
  swift: boolean;
  /** Doc 06 Cloaked: untargetable outside a reveal radius, 50% splash. */
  cloaked: boolean;
  /** Doc 06 Lanternjaw: the light goes out, but not while it is biting. */
  uncloakOnAttack: boolean;
  boss: 'miniboss' | 'boss' | null;
  behavior: string;
  saboteurTarget: string | null;
  /** Doc 03: a ranged attacker stops at `rangedRange` and spits for `rangedDps`. */
  rangedDps: number;
  rangedRange: number;
  /** Doc 06 Regen: HP/s, paused 2 s by any single hit ≥25. */
  regen: number;
  onDeathSpawn: DeathSpawn | null;
  cloakCycle: CloakCycle | null;
  guarded: GuardRule | null;
  dive: DiveRule | null;
  bite: BiteRule | null;
  aura: AuraRule | null;
  threads: ThreadRule | null;
  pulse: PulseRule | null;
  grab: { everySeconds: number; disableSeconds: number } | null;
  enrage: { belowHpFraction: number; speedMult: number } | null;
  escort: {
    everySeconds: number;
    spawn: Array<{ species: string; count: number }>;
    /** Only from this HP fraction downward — the Sovereign's phase 2. */
    below?: number;
  } | null;
}

/** Doc 06: any single hit this big interrupts regeneration for two seconds. */
export const REGEN_BREAK_DAMAGE = 25;
export const REGEN_BREAK_SECONDS = 2;

/** Doc 06: a support unit keeps this far behind the front unless told otherwise. */
const STANDOFF = 4;

/** Doc 03: ranged fauna stop this far out; one number, not a per-species knob. */
const RANGED_RANGE = 4;

export const SPECIES: Record<string, SpeciesDef> = {};
for (const [key, raw] of Object.entries(enemiesJson.species)) {
  const s = raw as unknown as SpeciesDef & Record<string, unknown>;
  const ranged = (s.rangedDps as number | undefined) ?? 0;
  SPECIES[key] = {
    name: s.name,
    hp: s.hp,
    speed: s.speed,
    dps: s.dps,
    armor: (s.armor as number | undefined) ?? 0,
    swift: (s.swift as boolean | undefined) ?? false,
    cloaked: (s.cloaked as boolean | undefined) ?? false,
    uncloakOnAttack: (s.uncloakOnAttack as boolean | undefined) ?? false,
    boss: (s.boss as 'miniboss' | 'boss' | undefined) ?? null,
    behavior: (s.behavior as string | undefined) ?? 'default',
    saboteurTarget: (s.saboteurTarget as string | undefined) ?? null,
    rangedDps: ranged,
    rangedRange: ranged > 0 ? RANGED_RANGE : 0,
    regen: (s.regen as number | undefined) ?? 0,
    onDeathSpawn: (s.onDeathSpawn as DeathSpawn | undefined) ?? null,
    cloakCycle: (s.cloakCycle as CloakCycle | undefined) ?? null,
    guarded: (s.guarded as GuardRule | undefined) ?? null,
    dive: (s.dive as DiveRule | undefined) ?? null,
    bite: (s.bite as BiteRule | undefined) ?? null,
    aura: s.aura ? { standoff: STANDOFF, ...(s.aura as Partial<AuraRule>) } as AuraRule : null,
    threads: (s.threads as ThreadRule | undefined) ?? null,
    pulse: (s.pulse as PulseRule | undefined) ?? null,
    grab: (s.grab as SpeciesDef['grab']) ?? null,
    enrage: (s.enrage as SpeciesDef['enrage']) ?? null,
    escort: (s.escort as SpeciesDef['escort']) ?? null,
  };
}
